import { useEffect, useMemo } from "react";
import { useSession } from "../../state/session.js";
import { isTypingTarget, moveInput } from "../camera/input.js";
import { navStore } from "../camera/nav.js";
import { useWorldState, worldStore } from "../store.js";
import { Dashboard } from "./Dashboard.js";
import { DriveHud } from "./DriveHud.js";
import { GaleriPanel } from "./GaleriPanel.js";
import { useVehicleT } from "./i18n.js";
import { TopUpDialog, WalletPill } from "./TopUp.js";
import { useVehicles, vehicleStore } from "./store.js";
import { installVehicleHook } from "./hook.js";
import { vehicleAudio } from "./audio/audio.js";
import "./vehicles.css";

/**
 * The DOM side of vehicles, mounted once next to the navigation HUD: the Galeri panel, the "E – Get
 * in" prompt, the driving HUD and the keyboard (E, 6). The 3D side is `VehiclesLayer` and
 * `DriveRig` inside the canvas.
 */
export function VehiclesUi() {
  const { user } = useSession();
  useEffect(() => {
    if (user?.id) vehicleStore.setSelf(user.id);
  }, [user?.id]);
  // The catalog and the user's cars load once the world is on screen.
  const status = useWorldState(worldStore).status;
  useEffect(() => {
    if (status === "ready") void vehicleStore.refresh();
  }, [status]);
  useVehicleKeys();
  useEffect(() => installVehicleHook(), []);
  useEffect(() => vehicleAudio.install(), []);
  return (
    <>
      <Prompt />
      <WalletPill />
      <GaleriPanel />
      <DriveHud />
      <Dashboard />
      <TopUpDialog />
    </>
  );
}

/** E gets in a nearby car or opens the dealership (before the door key sees it), E again gets out. */
function useVehicleKeys(): void {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      const s = vehicleStore.getState();
      if (e.key === "6" && s.drivingId) {
        const order = ["chase", "cockpit", "hood"] as const;
        vehicleStore.camMode = order[(order.indexOf(vehicleStore.camMode) + 1) % order.length]!;
        e.stopImmediatePropagation();
        return;
      }
      if (s.drivingId && e.code === "KeyH") {
        vehicleAudio.hornOn();
        e.stopImmediatePropagation();
        return;
      }
      if (s.drivingId && ["KeyZ", "KeyC", "KeyX"].includes(e.code)) {
        const want = e.code === "KeyZ" ? "left" : e.code === "KeyC" ? "right" : "hazard";
        vehicleStore.indicator = vehicleStore.indicator === want ? "off" : want;
        e.stopImmediatePropagation();
        return;
      }
      if (s.drivingId && /^[1-5]$/.test(e.key)) {
        // The other camera modes would leave the car running with no one at the wheel.
        e.stopImmediatePropagation();
        return;
      }
      if (e.key === "6") {
        // Mode 6 only exists inside a vehicle.
        e.stopImmediatePropagation();
        return;
      }
      if (s.topUp.open) return;
      if (e.code === "Escape" && s.galeri.open) {
        vehicleStore.closeGaleri();
        return;
      }
      if (e.code !== "KeyE") return;
      if (s.drivingId) {
        e.stopImmediatePropagation();
        moveInput.reset();
        vehicleStore.requestExit();
        return;
      }
      if (s.galeri.open) return;
      if (s.near && navStore.getState().inside === null) {
        e.stopImmediatePropagation();
        activate(s.near);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === "KeyH") vehicleAudio.hornOff();
    };
    // Capture phase on window: runs before the navigation key handler, which would walk into a door.
    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
    };
  }, []);
}

export function activate(
  near: NonNullable<ReturnType<typeof vehicleStore.getState>["near"]>,
): void {
  if (near.kind === "vehicle") void vehicleStore.enter(near.id);
  else vehicleStore.openGaleri(near.id);
}

function Prompt() {
  const t = useVehicleT();
  const near = useVehicles((s) => s.near);
  const driving = useVehicles((s) => s.drivingId);
  const open = useVehicles((s) => s.galeri.open);
  const coarse = useMemo(() => globalThis.matchMedia?.("(pointer: coarse)").matches ?? false, []);
  if (!near || driving || open) return null;
  const text =
    near.kind === "vehicle"
      ? coarse
        ? t("tapGetIn")
        : t("pressE")
      : coarse
        ? t("tapGaleri")
        : t("pressEGaleri");
  return (
    <button
      type="button"
      className="veh-prompt"
      data-testid="vehicle-prompt"
      data-kind={near.kind}
      onClick={() => activate(near)}
    >
      {text}
    </button>
  );
}
